/**
 * ChoiceGroup — unified compound for any vertical selection list.
 *
 * Three `kind`s sharing one declarative API:
 *
 *   - `'radio'`    — pick exactly one.
 *                    `value: string`, `onChange(next: string) => void`.
 *
 *   - `'checkbox'` — pick any subset; supports nested-children TREE shape
 *                    with auto-derived parent state (`indeterminate` when
 *                    some leaves are on, `true` when all are, `false`
 *                    when none).
 *                    `value: readonly string[]`,
 *                    `onChange(next: string[]) => void`.
 *
 *   - `'toggle'`   — settings-style list of independent on/off switches.
 *                    Each item can carry its own `color` (primary / good /
 *                    bad / alert) to flag positive, destructive, or risky
 *                    actions. Returning a `Promise<void>` from `onChange`
 *                    drives the per-Toggle optimistic flow with spinner
 *                    + bad-color flash on reject (see Toggle atom).
 *                    `value: readonly string[]`,
 *                    `onChange(next: string[]) => void | Promise<void>`.
 *
 * Items are declarative — `items: ChoiceItem[]`. Each item can carry:
 *   - `label` (required)       — primary text (or any ReactNode).
 *   - `description`            — secondary muted line below the label.
 *                                Composes into the atom's `description`
 *                                prop so the 2-line layout is identical
 *                                to standalone Radio / Checkbox / Toggle.
 *   - `leftAdornment`          — icon / avatar BETWEEN the glyph (radio,
 *                                checkbox; ignored for toggle — switch
 *                                lives at the right edge) and the label.
 *   - `rightAdornment`         — badge / price / hint at the trailing
 *                                edge (radio / checkbox only).
 *   - `disabled`               — per-item disable (group `disabled` OR's).
 *   - `color`                   — per-item color for `kind="toggle"` only;
 *                                overrides the group's `color`.
 *   - `children?: ChoiceItem[]`— TREE only in `kind="checkbox"`. Parents
 *                                derive state from leaves; clicking a
 *                                parent batches every ENABLED descendant.
 *                                Disabled leaves keep their membership
 *                                across batch toggles. Silently flattened
 *                                in radio / toggle mode.
 *
 * `value` arrays for `kind="checkbox"` and `kind="toggle"` contain LEAF
 * values only — parent values are derived on every render, so external
 * state stays minimal and predictable.
 *
 * Layout is always a flush column (no gap — system rule). For horizontal
 * selection use `<SegmentedButton mode="radio" | "checkbox" | "button">`.
 *
 * @format
 * @see Radio, Checkbox, Toggle for the underlying atoms.
 * @see FormField for label / helper / error chrome composition.
 */

import {forwardRef, useCallback, useMemo, type ReactNode} from 'react';

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

import {Radio, RadioGroup, RADIO_SIZES, type RadioSize} from 'ui/components/atoms/Radio';
import {Checkbox, type CheckboxState} from 'ui/components/atoms/Checkbox';
import {Toggle, type ToggleColor} from 'ui/components/atoms/Toggle';

// ── Public enums + types ──────────────────────────────────────────────

/** Discriminator for ChoiceGroup. Drives the shape of `value` / `onChange`. */
export const CHOICE_GROUP_KINDS = ['radio', 'checkbox', 'toggle'] as const;
export type ChoiceGroupKind = (typeof CHOICE_GROUP_KINDS)[number];

/** Sizes are shared with Radio + Checkbox so a group reads the same scale. */
export const CHOICE_GROUP_SIZES = RADIO_SIZES;
export type ChoiceGroupSize = RadioSize;

/** Side of the label relative to the glyph (radio / checkbox only). */
export const CHOICE_GROUP_LABEL_POSITIONS = ['start', 'end'] as const;
export type ChoiceGroupLabelPosition = (typeof CHOICE_GROUP_LABEL_POSITIONS)[number];

export interface ChoiceItem {
  /** Stable identifier. */
  value: string;
  /** Primary visible label. */
  label: ReactNode;
  /** Optional secondary muted line shown below `label`. */
  description?: ReactNode;
  /** Slot rendered between the glyph and the label (icon, avatar…). */
  leftAdornment?: ReactNode;
  /** Slot rendered at the row's trailing edge (badge, price, hint…). */
  rightAdornment?: ReactNode;
  /** Per-item disable; group-level `disabled` cascades regardless. */
  disabled?: boolean;
  /**
   * Per-item color. Overrides the group's `color` for this single item.
   * Works for all three kinds (radio / checkbox / toggle).
   */
  color?: ToggleColor;
  /**
   * Children — only meaningful for `kind="checkbox"`. Parents derive their
   * state from descendants (true / false / 'indeterminate'); clicking a
   * parent batches every enabled descendant. Ignored for `kind="radio"`.
   */
  children?: ChoiceItem[];
}

// Discriminated union — kind decides the shape of value/onChange.
interface RadioModeProps {
  /** Pick exactly one — `value`/`onChange` operate on a single string. */
  kind: 'radio';
  /** The currently-selected item's `value`. */
  value: string;
  /** Fires when the user picks a different item. */
  onChange: (next: string) => void;
}

interface CheckboxModeProps {
  /** Pick any subset of items — `value` is an array of selected leaves. */
  kind: 'checkbox';
  /**
   * Currently-selected LEAF values. Parent values are NEVER stored — they
   * are derived on every render from descendant membership.
   */
  value: readonly string[];
  /**
   * Fires with the new leaf set after a click. The array is sorted in
   * original `items` order so listeners get a predictable sequence
   * regardless of which item was clicked.
   */
  onChange: (next: string[]) => void;
}

interface ToggleModeProps {
  /** Settings-style list of independent on/off switches. */
  kind: 'toggle';
  /**
   * Currently-ON item values. Same shape as `kind="checkbox"`. Pass a
   * `Set`-style array of "enabled" item values.
   */
  value: readonly string[];
  /**
   * Fires with the new ON set in original `items` order. Returning a
   * `Promise<void>` activates the per-Toggle optimistic flow inside the
   * atom: the clicked toggle flips immediately, shows a spinner after
   * 500 ms if the promise hasn't settled, and bad-color-flashes + reverts
   * if the promise rejects.
   */
  onChange: (next: string[]) => void | Promise<void>;
}

interface CommonProps {
  // ── Required content ──────────────────────────────────────────────
  /**
   * The list to render — each entry becomes one Radio / Checkbox /
   * Toggle row. Items must have a stable `value` (used as React key
   * and as the identity emitted by `onChange`).
   */
  items: readonly ChoiceItem[];

  // ── Visual (cascades to every item) ───────────────────────────────
  /**
   * Default `'m'`. Cascades to every Radio / Checkbox / Toggle in the
   * group so the row metric stays consistent across kinds.
   */
  size?: ChoiceGroupSize;
  /**
   * Default `'primary'`. Cascades to every Radio / Checkbox / Toggle.
   * Use `'good'` / `'bad'` / `'alert'` to colour the active glyph or
   * switch semantically. Individual items can override via `item.color`.
   */
  color?: ToggleColor;

  // ── Content ───────────────────────────────────────────────────────
  /**
   * Default `'end'`. Ignored for `kind="toggle"` — toggles are always
   * rendered with the label on the LEFT and the switch on the right.
   */
  labelPosition?: ChoiceGroupLabelPosition;

  // ── Behaviour ─────────────────────────────────────────────────────
  /** Default `false`. Cascades to every item; OR'd with per-item `disabled`. */
  disabled?: boolean;
  /**
   * Accessible name for the group wrapper. Recommended — without it
   * screen readers announce the group with no context.
   */
  ariaLabel?: string;
  /**
   * Pass-through className applied to the outer wrapper. Use only for
   * one-off positioning overrides — anything color- or size-related
   * should go through the typed props.
   */
  className?: string;
}

export type ChoiceGroupProps = CommonProps & (RadioModeProps | CheckboxModeProps | ToggleModeProps);

// ── Item-content composer ─────────────────────────────────────────────

const ItemRow = styled(Box)(({theme}) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  width: '100%',
  minWidth: 0,
}));

const Adornment = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  flexShrink: 0,
});

/**
 * Compose `leftAdornment + label + rightAdornment` into a single ReactNode
 * that we hand to Radio / Checkbox via their `label` slot. Description is
 * NOT composed here — it goes through the atom's own `description` prop
 * so the 2-line layout (top-aligned glyph, muted second line) is owned
 * by the atom and works the same for standalone use.
 */
function renderItemLabel(it: ChoiceItem): ReactNode {
  const hasAdornments = it.leftAdornment != null || it.rightAdornment != null;
  if (!hasAdornments) return it.label;
  return (
    <ItemRow>
      {it.leftAdornment != null && <Adornment>{it.leftAdornment}</Adornment>}
      <Box component='span' style={{flex: '1 1 auto', minWidth: 0}}>
        {it.label}
      </Box>
      {it.rightAdornment != null && <Adornment>{it.rightAdornment}</Adornment>}
    </ItemRow>
  );
}

// ── Tree helpers (checkbox-only) ──────────────────────────────────────

/** Recursively collect every leaf value under an item (or the item itself). */
function collectLeaves(it: ChoiceItem, out: string[]): void {
  if (!it.children || it.children.length === 0) {
    out.push(it.value);
    return;
  }
  for (const c of it.children) collectLeaves(c, out);
}

/** Pre-order flatten (parents before children) — used only when rendering as a flat list. */
function flattenItems(items: readonly ChoiceItem[]): ChoiceItem[] {
  const out: ChoiceItem[] = [];
  const walk = (it: ChoiceItem) => {
    out.push(it);
    if (it.children) for (const c of it.children) walk(c);
  };
  for (const it of items) walk(it);
  return out;
}

/**
 * Compute an item's effective state given the current leaf set.
 * - leaf: `true` / `false` from set membership.
 * - parent: `true` if every descendant-leaf is in the set, `false` if none,
 *   `'indeterminate'` if some.
 */
function deriveState(it: ChoiceItem, set: Set<string>): CheckboxState {
  if (!it.children || it.children.length === 0) return set.has(it.value);
  const leaves: string[] = [];
  collectLeaves(it, leaves);
  let on = 0;
  for (const v of leaves) if (set.has(v)) on += 1;
  if (on === 0) return false;
  if (on === leaves.length) return true;
  return 'indeterminate';
}

// ── Layout ────────────────────────────────────────────────────────────

const CheckboxGroupWrap = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  // System rule: choice rows are always flush (gap 0). Not exposed as a prop.
  gap: 0,
  width: '100%',
});

/** Each tree level adds this much paddingLeft to its children's row. */
const TREE_LEVEL_INDENT = 28;

// ── Component ─────────────────────────────────────────────────────────

export const ChoiceGroup = forwardRef<HTMLDivElement, ChoiceGroupProps>(
  function ChoiceGroup(props, ref) {
    const {
      items,
      size = 's',
      disabled = false,
      labelPosition = 'end',
      color = 'primary',
      ariaLabel,
      className,
    } = props;

    if (props.kind === 'radio') {
      // Radios don't have tree semantics — flatten any accidentally-nested
      // input so the group still renders sensibly. Tree shape is silently
      // ignored (documented).
      const flat = flattenItems(items);
      return (
        <Box ref={ref} className={className} aria-label={ariaLabel}>
          <RadioGroup
            value={props.value}
            onChange={props.onChange}
            size={size}
            disabled={disabled}
            labelPosition={labelPosition}
            color={color}
          >
            {flat.map(it => (
              <Radio
                key={it.value}
                value={it.value}
                label={renderItemLabel(it)}
                description={it.description}
                disabled={it.disabled}
                color={it.color}
              />
            ))}
          </RadioGroup>
        </Box>
      );
    }

    if (props.kind === 'checkbox') {
      return (
        <CheckboxTreeRoot
          ref={ref}
          items={items}
          value={props.value}
          onChange={props.onChange}
          size={size}
          disabled={disabled}
          labelPosition={labelPosition}
          color={color}
          ariaLabel={ariaLabel}
          className={className}
        />
      );
    }

    // kind === 'toggle' — settings-style list. Toggles don't support tree
    // semantics; any nested children are silently flattened (parents and
    // children become siblings).
    const flatToggle = flattenItems(items);
    return (
      <ToggleGroupRoot
        ref={ref}
        items={flatToggle}
        value={props.value}
        onChange={props.onChange}
        size={size}
        disabled={disabled}
        color={color}
        ariaLabel={ariaLabel}
        className={className}
      />
    );
  },
);

// ── Toggle-mode internals ─────────────────────────────────────────────

interface ToggleGroupRootProps {
  items: readonly ChoiceItem[];
  value: readonly string[];
  onChange: (next: string[]) => void | Promise<void>;
  size: ChoiceGroupSize;
  disabled: boolean;
  color: ToggleColor;
  ariaLabel?: string;
  className?: string;
}

const ToggleGroupRoot = forwardRef<HTMLDivElement, ToggleGroupRootProps>(function ToggleGroupRoot(
  {items, value, onChange, size, disabled, color, ariaLabel, className},
  ref,
) {
  const valueSet = useMemo(() => new Set(value), [value]);

  const handleItemChange = useCallback(
    (itemValue: string, nextOn: boolean) => {
      const nextSet = new Set(value);
      if (nextOn) nextSet.add(itemValue);
      else nextSet.delete(itemValue);
      // Preserve original item order on every emit so listeners get
      // a predictable sequence regardless of click target.
      const ordered: string[] = [];
      for (const it of items) if (nextSet.has(it.value)) ordered.push(it.value);
      // Pass through the Promise so the Toggle's spinner reflects the
      // group's async onChange directly.
      return onChange(ordered);
    },
    [items, onChange, value],
  );

  return (
    <CheckboxGroupWrap
      ref={ref}
      role='group'
      aria-label={ariaLabel}
      aria-disabled={disabled || undefined}
      className={className}
    >
      {items.map(it => (
        <Toggle
          key={it.value}
          checked={valueSet.has(it.value)}
          onChange={next => handleItemChange(it.value, next)}
          label={renderItemLabel(it)}
          description={it.description}
          size={size}
          color={it.color ?? color}
          disabled={disabled || it.disabled}
        />
      ))}
    </CheckboxGroupWrap>
  );
});

// ── Checkbox-mode internals (handles flat and tree shapes uniformly) ──

interface CheckboxTreeRootProps {
  items: readonly ChoiceItem[];
  value: readonly string[];
  onChange: (next: string[]) => void;
  size: ChoiceGroupSize;
  disabled: boolean;
  labelPosition: ChoiceGroupLabelPosition;
  color: ToggleColor;
  ariaLabel?: string;
  className?: string;
}

const CheckboxTreeRoot = forwardRef<HTMLDivElement, CheckboxTreeRootProps>(
  function CheckboxTreeRoot(
    {items, value, onChange, size, disabled, labelPosition, color, ariaLabel, className},
    ref,
  ) {
    const valueSet = useMemo(() => new Set(value), [value]);

    // Stable order: walk the tree once, collect every leaf in document order.
    // We emit `onChange` results in this order so listeners get a predictable
    // sequence regardless of click target.
    const orderedLeaves = useMemo(() => {
      const out: string[] = [];
      for (const it of items) collectLeaves(it, out);
      return out;
    }, [items]);

    const emit = useCallback(
      (nextSet: Set<string>) => {
        const ordered: string[] = [];
        for (const v of orderedLeaves) if (nextSet.has(v)) ordered.push(v);
        onChange(ordered);
      },
      [onChange, orderedLeaves],
    );

    const toggleSubtree = useCallback(
      (it: ChoiceItem, ancestorDisabled: boolean) => {
        // Compute the desired direction from CURRENT state of this subtree.
        const state = deriveState(it, valueSet);
        const next = new Set(valueSet);
        const leaves: string[] = [];
        collectLeaves(it, leaves);

        // Mark which leaves are click-eligible: enabled leaves of this
        // subtree. Disabled leaves keep their current membership.
        const leafItemMap = new Map<string, ChoiceItem>();
        const walk = (n: ChoiceItem) => {
          if (!n.children || n.children.length === 0) leafItemMap.set(n.value, n);
          else for (const c of n.children) walk(c);
        };
        walk(it);

        const wantOn = state !== true; // from false/indeterminate → on; from true → off
        for (const v of leaves) {
          const leafIt = leafItemMap.get(v);
          // Group-disabled or any-ancestor-disabled or self-disabled blocks the toggle.
          const leafDisabled = disabled || ancestorDisabled || it.disabled || leafIt?.disabled;
          if (leafDisabled) continue;
          if (wantOn) next.add(v);
          else next.delete(v);
        }
        emit(next);
      },
      [disabled, emit, valueSet],
    );

    return (
      <CheckboxGroupWrap
        ref={ref}
        role='group'
        aria-label={ariaLabel}
        aria-disabled={disabled || undefined}
        className={className}
      >
        {items.map(it => (
          <TreeNode
            key={it.value}
            item={it}
            depth={0}
            valueSet={valueSet}
            size={size}
            labelPosition={labelPosition}
            color={color}
            groupDisabled={disabled}
            ancestorDisabled={false}
            onToggle={toggleSubtree}
          />
        ))}
      </CheckboxGroupWrap>
    );
  },
);

interface TreeNodeProps {
  item: ChoiceItem;
  depth: number;
  valueSet: Set<string>;
  size: ChoiceGroupSize;
  labelPosition: ChoiceGroupLabelPosition;
  color: ToggleColor;
  groupDisabled: boolean;
  ancestorDisabled: boolean;
  onToggle: (item: ChoiceItem, ancestorDisabled: boolean) => void;
}

const NodeRow = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$indent: number}>(({$indent}) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  paddingLeft: $indent,
  boxSizing: 'border-box',
}));

function TreeNode({
  item,
  depth,
  valueSet,
  size,
  labelPosition,
  color,
  groupDisabled,
  ancestorDisabled,
  onToggle,
}: TreeNodeProps) {
  const state = deriveState(item, valueSet);
  const selfDisabled = groupDisabled || ancestorDisabled || item.disabled || false;
  const indent = depth * TREE_LEVEL_INDENT;
  const hasChildren = !!item.children && item.children.length > 0;

  return (
    <NodeRow $indent={indent}>
      <Checkbox
        checked={state}
        onChange={() => onToggle(item, ancestorDisabled)}
        label={renderItemLabel(item)}
        description={item.description}
        size={size}
        labelPosition={labelPosition}
        color={item.color ?? color}
        disabled={selfDisabled}
      />
      {hasChildren && (
        <>
          {item.children!.map(c => (
            <TreeNode
              key={c.value}
              item={c}
              depth={depth + 1}
              valueSet={valueSet}
              size={size}
              labelPosition={labelPosition}
              color={color}
              groupDisabled={groupDisabled}
              ancestorDisabled={selfDisabled}
              onToggle={onToggle}
            />
          ))}
        </>
      )}
    </NodeRow>
  );
}
